/**
 * Conversation History Drawer
 *
 * Shows list of past conversations with ability to:
 * - Resume a conversation
 * - Start a new conversation
 * - Delete conversations
 */

import { useState, useEffect, useMemo } from 'react';
import { Drawer, Box, Typography, List, ListItem, ListItemButton, ListItemText, IconButton, Button, Divider, TextField } from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import type { Conversation } from '@lingaai/types';

interface ConversationHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

export const ConversationHistoryDrawer = ({ open, onClose, userId, currentConversationId, onSelectConversation, onNewConversation }: ConversationHistoryDrawerProps) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const loadConversations = async () => {
    setLoading(true);
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/chat/conversations?user_id=${userId}`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering - search in title and message content
  const filteredConversations = useMemo(() => {
    if (!search) return conversations;
    const query = search.toLowerCase();
    return conversations.filter((conv) => {
      // Search in title
      if (conv.title?.toLowerCase().includes(query)) return true;

      // Search in message content (history is JSONB array)
      if (conv.history) {
        const historyStr = JSON.stringify(conv.history).toLowerCase();
        return historyStr.includes(query);
      }

      return false;
    });
  }, [conversations, search]);

  const deleteConversation = async (conversationId: string) => {
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/chat/conversations/${conversationId}?user_id=${userId}`, { method: 'DELETE' });
      if (response.ok) {
        // Remove from list
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        // If deleted current conversation, start new one
        if (conversationId === currentConversationId) {
          onNewConversation();
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  useEffect(() => {
    if (open) {
      loadConversations();
    }
  }, [open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 350, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Conversations</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField fullWidth size="small" placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </Box>

        {/* New Conversation Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              onNewConversation();
              onClose();
            }}
          >
            New Conversation
          </Button>
        </Box>

        <Divider />

        {/* Conversation List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {search ? 'No conversations found' : 'No conversations yet'}
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredConversations.map((conversation) => (
                <ListItem
                  key={conversation.id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conversation.id);
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{
                    bgcolor: conversation.id === currentConversationId ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      onSelectConversation(conversation.id);
                      onClose();
                    }}
                  >
                    <ListItemText
                      primary={conversation.title || 'Untitled'}
                      secondary={new Date(conversation.updated_at).toLocaleString()}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: { fontWeight: conversation.id === currentConversationId ? 600 : 400 },
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
